import { icons } from '../icons';
import React, { useMemo, useState } from 'react';
import cn from '../utils/cn';
import KoobEmptyState from './KoobEmptyState';
import KoobInputLabel from './KoobInputLabel';
import KoobSpinner from './KoobSpinner';
import type { KoobSelectValue } from './KoobInputSelect';

export interface KoobTreeItem {
  value: KoobSelectValue;
  label: string;
  /** ISO alpha-2 code — renders a flag-icon */
  code?: string;
  /** Leading visual (logo, icon) shown after the check slot */
  icon?: React.ReactNode;
  /** Muted text after the label */
  description?: string;
  children?: KoobTreeItem[];
}

export interface KoobTreeRowState {
  checked: boolean;
  indeterminate?: boolean;
}

/**
 * Searchable, alphabetically sorted, two-level list. Base for
 * KoobInputCountries / KoobInputRights.
 *
 * Checkbox mode: pass `value` (selected leaf values — childless parents are
 * leaves too) + `onValueChange`. A parent row aggregates its children.
 * Otherwise rows are inert and `renderTrailing` carries the interaction.
 */
type KoobInputTreeProps = {
  items: KoobTreeItem[];
  value?: KoobSelectValue[];
  onValueChange?: (value: KoobSelectValue[]) => void;
  /** Trailing content on a row (selects, badges, actions). */
  renderTrailing?: (
    item: KoobTreeItem,
    parent?: KoobTreeItem,
  ) => React.ReactNode;
  /** Text of the expand toggle for a parent with `count` children */
  childrenLabel?: (count: number) => string;
  /** Sort rows by label (default true) */
  sort?: boolean;
  searchable?: boolean;
  placeholder?: string;
  emptyTitle?: string;
  emptySearchTitle?: string;
  emptyIcon?: React.ReactNode;
  /** Fixed list height: sm = 12rem, md = 24rem (default) */
  height?: 'sm' | 'md';
  loading?: boolean;
  disabled?: boolean;
  error?: boolean;
  label?: React.ReactNode;
  required?: boolean;
  'data-testid'?: string;
  /** Not part of the API (see packages/ui/README.md) — stripped at runtime */
  className?: never;
};

const byLabel = (a: KoobTreeItem, b: KoobTreeItem) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

const sortTree = (items: KoobTreeItem[]): KoobTreeItem[] =>
  [...items]
    .sort(byLabel)
    .map((item) =>
      item.children
        ? { ...item, children: [...item.children].sort(byLabel) }
        : item,
    );

const KoobInputTree = React.forwardRef<HTMLDivElement, KoobInputTreeProps>(
  (
    {
      items,
      value,
      onValueChange,
      renderTrailing,
      childrenLabel = (count) => `${count} item${count > 1 ? 's' : ''}`,
      sort = true,
      searchable = true,
      placeholder = 'Search...',
      emptyTitle = 'Nothing available',
      emptySearchTitle = 'No results',
      emptyIcon = <i className="fa-regular fa-list text-2xl" />,
      height = 'md',
      loading = false,
      disabled = false,
      error = false,
      label,
      required = false,
      'data-testid': dataCy,
      className,
      ...rest
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState<Set<KoobSelectValue>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');

    const selectable = Boolean(onValueChange);
    const selected = value ?? [];

    const rows = useMemo(() => {
      const sorted = sort ? sortTree(items ?? []) : (items ?? []);
      if (!searchTerm) return sorted;
      const term = searchTerm.toLowerCase();
      return sorted.filter(
        (item) =>
          item.label.toLowerCase().includes(term) ||
          item.children?.some((c) => c.label.toLowerCase().includes(term)),
      );
    }, [items, searchTerm, sort]);

    const getState = (item: KoobTreeItem): KoobTreeRowState => {
      if (!item.children?.length) {
        return { checked: selected.includes(item.value) };
      }
      const count = item.children.filter((c) =>
        selected.includes(c.value),
      ).length;
      return {
        checked: count === item.children.length,
        indeterminate: count > 0 && count < item.children.length,
      };
    };

    const toggle = (item: KoobTreeItem) => {
      if (!onValueChange || disabled) return;
      const leaves = item.children?.length
        ? item.children.map((c) => c.value)
        : [item.value];
      onValueChange(
        getState(item).checked
          ? selected.filter((v) => !leaves.includes(v))
          : [...selected, ...leaves.filter((v) => !selected.includes(v))],
      );
    };

    const toggleExpansion = (v: KoobSelectValue) =>
      setExpanded((prev) => {
        const next = new Set(prev);
        next.has(v) ? next.delete(v) : next.add(v);
        return next;
      });

    const renderCheck = (state: KoobTreeRowState) => (
      <span
        className={cn(
          'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[10px] text-white transition-colors',
          state.checked || state.indeterminate
            ? 'border-orange-500 bg-orange-500'
            : 'border-gray-300 bg-white',
        )}
        aria-hidden
      >
        {state.checked && <i className={icons.check} />}
        {!state.checked && state.indeterminate && <i className={icons.minus} />}
      </span>
    );

    const renderRow = (item: KoobTreeItem, parent?: KoobTreeItem) => {
      const state = getState(item);
      const hasChildren = Boolean(item.children?.length);
      const isExpanded = expanded.has(item.value);
      const interactive = selectable && !disabled;

      return (
        <div
          role={selectable ? 'checkbox' : undefined}
          aria-checked={
            selectable
              ? state.indeterminate
                ? 'mixed'
                : state.checked
              : undefined
          }
          tabIndex={interactive ? 0 : -1}
          onClick={() => toggle(item)}
          onKeyDown={(e) => {
            if ((e.key === ' ' || e.key === 'Enter') && interactive) {
              e.preventDefault();
              toggle(item);
            }
          }}
          className={cn(
            'flex select-none items-center gap-3 px-3 text-sm transition-colors focus:outline-none focus-visible:bg-gray-50',
            parent ? 'py-2 pl-12' : 'py-2.5',
            interactive && 'cursor-pointer hover:bg-gray-50',
            disabled ? 'text-gray-400' : 'text-gray-700',
            state.checked && !disabled && 'text-gray-900',
          )}
          data-testid={`${dataCy ?? 'tree'}-${item.value}`}
        >
          {selectable && renderCheck(state)}

          {item.code && (
            <span
              className={`flag-icon flag-icon-${item.code.toLowerCase()} h-4 w-5 shrink-0 rounded-sm`}
              aria-hidden
            />
          )}
          {item.icon && (
            <span className="flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center [&>*]:h-5 [&>*]:w-auto [&>*]:max-w-14 [&>*]:object-contain">
              {item.icon}
            </span>
          )}

          <span className="min-w-0 flex-1 truncate">
            <span className={cn(!parent && 'font-medium')}>{item.label}</span>
            {item.description && (
              <span className="ml-1.5 text-gray-400">{item.description}</span>
            )}
            {hasChildren && (
              <span className="ml-1.5 text-xs text-gray-400">
                {childrenLabel(item.children?.length ?? 0)}
              </span>
            )}
          </span>

          {(renderTrailing || hasChildren) && (
            <span
              className="flex shrink-0 items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {renderTrailing?.(item, parent)}
              {hasChildren && (
                <button
                  type="button"
                  onClick={() => toggleExpansion(item.value)}
                  disabled={disabled}
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed"
                  aria-expanded={isExpanded}
                  aria-label={childrenLabel(item.children?.length ?? 0)}
                >
                  <i
                    className={cn(
                      'fa-regular fa-chevron-down text-xs transition-transform duration-200',
                      isExpanded && 'rotate-180',
                    )}
                  />
                </button>
              )}
            </span>
          )}
        </div>
      );
    };

    const control = (
      <div
        ref={ref}
        data-testid={dataCy}
        className={cn(
          'w-full overflow-hidden rounded-lg border border-solid bg-white',
          error ? 'border-red-500' : 'border-gray-200',
          disabled && 'bg-gray-100 opacity-50',
        )}
        {...rest}
      >
        {searchable && (
          <div className="flex items-center gap-2 border-b border-solid border-gray-200 px-3">
            <i
              className="fa-regular fa-magnifying-glass text-sm text-gray-400"
              aria-hidden
            />
            <input
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={disabled}
              className="w-full border-0 bg-transparent p-0 py-2.5 text-base placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed md:text-sm"
            />
          </div>
        )}

        <div
          className={cn('overflow-y-auto', height === 'sm' ? 'h-48' : 'h-96')}
        >
          {loading ? (
            <div className="flex justify-center py-10">
              <KoobSpinner />
            </div>
          ) : rows.length === 0 ? (
            <KoobEmptyState
              variant="plain"
              icon={emptyIcon}
              title={searchTerm ? emptySearchTitle : emptyTitle}
              description={
                searchTerm ? 'Try adjusting your search.' : undefined
              }
            />
          ) : (
            <ul className="m-0 list-none divide-y divide-gray-100 p-0">
              {rows.map((item) => (
                <li key={item.value}>
                  {renderRow(item)}
                  {Boolean(item.children?.length) &&
                    expanded.has(item.value) && (
                      <ul className="m-0 list-none divide-y divide-gray-100 border-t border-solid border-gray-100 bg-gray-50/60 p-0">
                        {item.children?.map((child) => (
                          <li key={child.value}>{renderRow(child, item)}</li>
                        ))}
                      </ul>
                    )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );

    return label != null ? (
      <KoobInputLabel label={label} required={required} disabled={disabled}>
        {control}
      </KoobInputLabel>
    ) : (
      control
    );
  },
);

KoobInputTree.displayName = 'KoobInputTree';

export default KoobInputTree;
